/**
 * Registre CURATÉ d'icônes de TYPE d'item, sélectionnables par un template (#124 — génération
 * 100 % paramétrable). Jusqu'ici l'icône d'un kind était figée sur les 7 standard et un kind CUSTOM
 * retombait tous sur le même glyphe générique (`Shapes`) : sur un board mêlant plusieurs types
 * custom (« garantie », « exigence », « risque »…), rien ne les distinguait à l'œil.
 *
 * Un template peut désormais associer une icône à CHAQUE type (`itemTypes[].icon` = une CLÉ de ce
 * registre). On expose un ENSEMBLE FERMÉ et validé — jamais un import dynamique de tout lucide, ni
 * un nom arbitraire venu de la base — pour garder un bundle borné et une entrée sûre : une clé
 * inconnue est simplement ignorée (repli sur l'icône du kind, puis `Shapes`), jamais un crash.
 *
 * Les 7 clés standard (idea/cadrage/epic/feature/story/task/bug) reprennent EXACTEMENT les glyphes
 * de `KIND_ICON` (WorkItemBadges) : un template qui ne choisit pas d'icône reste identique à avant.
 */
import {
  Lightbulb, Compass, Layers, Package, BookOpen, SquareCheck, Bug, Shapes,
  Shield, ShieldCheck, FileText, ClipboardList, Wrench, Target, Flag, Zap, Rocket,
  Database, Server, Users, User, Coins, DollarSign, Calendar, GitBranch, Milestone,
  Puzzle, Box, Boxes, Star, AlertTriangle, CheckCircle2, Workflow, Component, Gauge,
  type LucideIcon,
} from 'lucide-react'

/** Nom lisible (FR) d'une icône du registre, pour le sélecteur de l'éditeur de template. */
export interface KindIconChoice {
  name: string
  label: string
  Icon: LucideIcon
}

// L'ensemble FERMÉ. L'ordre est celui du sélecteur : d'abord les 7 glyphes standard (pour
// re-iconer un type standard), puis une réserve utile aux domaines métier (assurance, finance,
// sécurité, delivery…). Ajouter une icône = importer le glyphe lucide + une ligne ici.
export const KIND_ICON_CHOICES: readonly KindIconChoice[] = [
  { name: 'lightbulb', label: 'Ampoule (idée)', Icon: Lightbulb },
  { name: 'compass', label: 'Boussole (cadrage)', Icon: Compass },
  { name: 'layers', label: 'Couches (epic)', Icon: Layers },
  { name: 'package', label: 'Paquet (feature)', Icon: Package },
  { name: 'book-open', label: 'Livre (story)', Icon: BookOpen },
  { name: 'square-check', label: 'Case cochée (tâche)', Icon: SquareCheck },
  { name: 'bug', label: 'Bug', Icon: Bug },
  { name: 'shield', label: 'Bouclier', Icon: Shield },
  { name: 'shield-check', label: 'Bouclier validé', Icon: ShieldCheck },
  { name: 'file-text', label: 'Document', Icon: FileText },
  { name: 'clipboard-list', label: 'Liste', Icon: ClipboardList },
  { name: 'wrench', label: 'Clé (technique)', Icon: Wrench },
  { name: 'target', label: 'Cible (objectif)', Icon: Target },
  { name: 'flag', label: 'Drapeau', Icon: Flag },
  { name: 'zap', label: 'Éclair', Icon: Zap },
  { name: 'rocket', label: 'Fusée', Icon: Rocket },
  { name: 'database', label: 'Base de données', Icon: Database },
  { name: 'server', label: 'Serveur', Icon: Server },
  { name: 'users', label: 'Personnes', Icon: Users },
  { name: 'user', label: 'Personne', Icon: User },
  { name: 'coins', label: 'Pièces', Icon: Coins },
  { name: 'dollar-sign', label: 'Montant', Icon: DollarSign },
  { name: 'calendar', label: 'Calendrier', Icon: Calendar },
  { name: 'git-branch', label: 'Branche', Icon: GitBranch },
  { name: 'milestone', label: 'Jalon', Icon: Milestone },
  { name: 'puzzle', label: 'Pièce de puzzle', Icon: Puzzle },
  { name: 'box', label: 'Boîte', Icon: Box },
  { name: 'boxes', label: 'Boîtes', Icon: Boxes },
  { name: 'star', label: 'Étoile', Icon: Star },
  { name: 'alert-triangle', label: 'Alerte (risque)', Icon: AlertTriangle },
  { name: 'check-circle', label: 'Validé', Icon: CheckCircle2 },
  { name: 'workflow', label: 'Flux', Icon: Workflow },
  { name: 'component', label: 'Composant', Icon: Component },
  { name: 'gauge', label: 'Jauge', Icon: Gauge },
]

const REGISTRE: Record<string, LucideIcon> = Object.fromEntries(
  KIND_ICON_CHOICES.map((c) => [c.name, c.Icon]),
)

/**
 * L'icône lucide d'une CLÉ du registre, ou `undefined` si la clé est vide/inconnue. Volontairement
 * TOLÉRANT : une clé d'icône obsolète ou mal saisie dans un template ne casse jamais le rendu — le
 * composant appelant retombe alors sur l'icône dérivée du kind, puis sur `Shapes`.
 */
export function resoudreIcone(nom?: string | null): LucideIcon | undefined {
  const cle = (nom ?? '').trim()
  return cle ? REGISTRE[cle] : undefined
}

/** L'icône générique de repli (kind custom SANS icône ni glyphe standard). */
export const ICONE_REPLI: LucideIcon = Shapes
